import React, { useEffect, useState } from "react";
import { Bell, BellOff, RefreshCw, Send, Search } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import LoadErrorBanner, { describeLoadError } from "@/components/admin/LoadErrorBanner";

import OnboardingProgressCard, { shortDate } from "@/components/admin/OnboardingProgressCard";

export default function OnboardingNudgePreview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try { const { data } = await api.get("/admin/onboarding/nudge-preview"); setData(data); }
    catch (e) { setError(describeLoadError(e, "nudge preview")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    const n = data?.recipients?.length || 0;
    if (!n || !window.confirm(`Send the stalled-onboarding nudge now to these ${n} supplier${n === 1 ? "" : "s"}? This is exactly what the next hourly sweep would do.`)) return;
    setSending(true);
    try {
      const { data: r } = await api.post("/admin/onboarding/nudge-stalled");
      toast.success(`Nudge sweep: ${r.stats.sent} sent · ${r.stats.complete} already complete · ${r.stats.too_old} outside 7-day window`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Sweep failed"); }
    finally { setSending(false); }
  };

  const snooze = async (r) => {
    try {
      await api.post(`/admin/onboarding/nudge-snooze/${r.id}`);
      toast.success(`${r.company || r.email} snoozed for 7 days`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not snooze"); }
  };
  const unsnooze = async (r) => {
    try { await api.delete(`/admin/onboarding/nudge-snooze/${r.id}`); toast.success("Snooze removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not remove snooze"); }
  };

  const recipients = data?.recipients || [];
  const snoozed = data?.snoozed || [];
  const [lookupQ, setLookupQ] = useState("");
  const [lookup, setLookup] = useState({ items: null, busy: false, error: null });
  const runLookup = async (e) => {
    e?.preventDefault();
    if (!lookupQ.trim()) return;
    setLookup({ items: null, busy: true, error: null });
    try { const { data } = await api.get(`/admin/onboarding/progress?q=${encodeURIComponent(lookupQ.trim())}`); setLookup({ items: data.items || [], busy: false, error: null }); }
    catch (err) { setLookup({ items: null, busy: false, error: describeLoadError(err, "supplier progress") }); }
  };
  const s = data?.stats || {};
  return (
    <div className="bg-white border border-[#E8ECE9] p-6" data-testid="onboarding-nudge-preview">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-xl text-[#11211A] flex items-center gap-2"><Bell className="w-4 h-4 text-[#B35C4A]" /> Next onboarding nudge</div>
          <p className="text-xs text-[#7D8C85] mt-0.5">Who the hourly sweep would email right now — signed up {data?.rules?.delay_hours || 24}h–{data?.rules?.window_days || 7}d ago, at {data?.rules?.threshold_pct || 50}% or less, with at least one setup step genuinely missing. Nothing is sent from this preview.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-right mr-2">
            <div className={`font-serif text-2xl ${recipients.length ? "text-[#B35C4A]" : "text-[#11211A]"}`} data-testid="nudge-preview-count">{loading ? "…" : recipients.length}</div>
            <div className="text-[10px] uppercase tracking-wider text-[#7D8C85]">would be emailed</div>
          </div>
          <button type="button" onClick={load} className="btn-ghost text-xs" title="Refresh preview" data-testid="nudge-preview-refresh"><RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /></button>
          <button type="button" onClick={runNow} disabled={sending || !recipients.length} className="btn-primary text-xs disabled:opacity-50" data-testid="nudge-preview-send"><Send className="w-3.5 h-3.5" /> {sending ? "Sending…" : "Send now"}</button>
        </div>
      </div>
      <LoadErrorBanner error={error} onRetry={load} testId="nudge-preview-error" />
      <form onSubmit={runLookup} className="mb-4 border border-[#E8ECE9] bg-[#FAFAF8] p-3" data-testid="progress-lookup">
        <div className="flex flex-wrap items-center gap-2">
          <Search className="w-3.5 h-3.5 text-[#7D8C85]" />
          <input value={lookupQ} onChange={(e) => setLookupQ(e.target.value)} placeholder="Check any supplier — company or email (e.g. Bambusa, RANS)" className="flex-1 min-w-[240px] px-3 py-1.5 text-sm border border-[#C4CFC9] rounded-sm bg-white" data-testid="progress-lookup-input" />
          <button type="submit" disabled={lookup.busy || !lookupQ.trim()} className="btn-primary text-xs disabled:opacity-50" data-testid="progress-lookup-submit">{lookup.busy ? "Checking…" : "Check completion"}</button>
        </div>
        <LoadErrorBanner error={lookup.error} testId="progress-lookup-error" />
        {lookup.items && lookup.items.length === 0 && <div className="text-xs text-[#7D8C85] mt-2" data-testid="progress-lookup-empty">No supplier matches "{lookupQ}".</div>}
        {lookup.items?.map((s) => <OnboardingProgressCard key={s.id} s={s} />)}
      </form>
      {!loading && !error && (
        <>
          <div className="flex flex-wrap gap-4 text-[11px] text-[#7D8C85] mb-3" data-testid="nudge-preview-stats">
            <span>{s.scanned ?? 0} scanned</span><span>· {s.already_nudged ?? 0} already nudged</span><span>· {s.snoozed ?? 0} snoozed</span><span>· {s.complete ?? 0} fully set up</span><span>· {s.too_old ?? 0} outside 7-day window</span><span>· {s.not_stalled ?? 0} above threshold</span><span>· {s.not_ready ?? 0} too new / not eligible</span>
          </div>
          {recipients.length === 0 ? (
            <div className="bg-[#F0EFEA] p-4 text-center text-sm text-[#7D8C85]" data-testid="nudge-preview-empty">Nobody is due a nudge on the next sweep.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-wider text-[#7D8C85]"><tr><th className="py-1.5 pr-3">Supplier</th><th className="py-1.5 pr-3">Signed up</th><th className="py-1.5 pr-3">Progress</th><th className="py-1.5 pr-3">Missing</th><th className="py-1.5"></th></tr></thead>
              <tbody>
                {recipients.map((r) => (
                  <tr key={r.id} className="border-t border-[#E8ECE9]" data-testid={`nudge-preview-row-${r.id}`}>
                    <td className="py-2 pr-3"><div className="text-[#11211A]">{r.company || r.name || "—"}</div><div className="text-[11px] text-[#7D8C85]">{r.email} · {r.tier}</div></td>
                    <td className="py-2 pr-3 text-[#4A5550] whitespace-nowrap">{shortDate(r.created_at)}</td>
                    <td className="py-2 pr-3 text-[#B35C4A] whitespace-nowrap">{r.percent}% · {r.done}/{r.total}</td>
                    <td className="py-2 pr-3 text-xs text-[#4A5550]">{r.missing.join(" · ")}</td>
                    <td className="py-2 text-right"><button type="button" onClick={() => snooze(r)} title="Skip this supplier for the next 7 days of sweeps" className="text-[10px] uppercase tracking-widest font-semibold border border-[#C4CFC9] text-[#7D8C85] px-2 py-1 hover:border-[#264839] hover:text-[#264839] transition inline-flex items-center gap-1" data-testid={`nudge-snooze-${r.id}`}><BellOff className="w-3 h-3" /> Snooze</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {snoozed.length > 0 && (
            <div className="mt-4 border-t border-[#E8ECE9] pt-3" data-testid="nudge-snoozed-list">
              <div className="text-[10px] uppercase tracking-wider text-[#7D8C85] mb-2">Snoozed ({snoozed.length})</div>
              <div className="flex flex-wrap gap-2">
                {snoozed.map((r) => (
                  <span key={r.id} className="inline-flex items-center gap-2 bg-[#F0EFEA] text-xs text-[#4A5550] px-2.5 py-1" data-testid={`nudge-snoozed-${r.id}`}>
                    {r.company || r.email} <span className="text-[#9AA6A0]">until {shortDate(r.snoozed_until)}</span>
                    <button type="button" onClick={() => unsnooze(r)} className="text-[#264839] underline underline-offset-2" data-testid={`nudge-unsnooze-${r.id}`}>Unsnooze</button>
                  </span>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
